import PropTypes from 'prop-types'

import { anim, pv } from '../motion'
import { COLORS, Shape } from '../primitives'
import { Desk, Heart, INK, Person, Plant } from '../v3/figures'
import { IsoRect, iso } from '../v3/iso'
import {
  Chair,
  DESKS,
  Floor,
  FloorLabels,
  Lounge,
  Machine,
  PLANTS,
  Walls
} from '../v3/world'
import { atB, inB } from './beats'

/**
 * Das Büro der Version 4. Baut auf den Teilen aus v3 auf, mit einem
 * zusätzlichen freien Platz im Vertrieb (fürs Kreuzmatching) und einer
 * Fläche, auf der aus Bewerbungen Menschen werden.
 */

export const SPOTS = {
  door: { x: 22.2, y: 0.3 },
  mara: { x: 10.9, y: 9.9 },
  interview: { x: 13.1, y: 9.2 },
  csSeat: { x: 21.9, y: 16.6 },
  vSeat: { x: 4.4, y: 20.6 },
  front: [
    { x: 9.4, y: 12.6 },
    { x: 11.2, y: 12.9 },
    { x: 13, y: 13.2 }
  ]
}

// Formen stehen hier vereinfacht für Stärken bzw. Anforderungen
export const SILHOUETTES = {
  cs: ['dot-pink', 'tri-yellow', 'sq-green'],
  v: ['sq-blue', 'dot-yellow', 'tri-pink']
}

export const parseShape = (token) => {
  const [kind, color] = token.split('-')
  return { kind, color }
}

const spot = (c, r) => ({ x: 9.4 + c * 1.8, y: 15.4 + r * 2.2 })

const OTHER_LOOKS = [
  { body: '#B2B4B6', skin: '#F2C9A5', hair: '#7A4A2A' },
  { body: '#8FA4D8', skin: '#E0AC69', hair: '#2B1D14' },
  { body: '#C9A7F2', skin: '#F1D3B3', hair: '#E6C27A' },
  { body: '#7FD3E6', skin: '#8D5524', hair: INK },
  { body: '#F2B48F', skin: '#C68B59', hair: '#2B1D14' },
  { body: '#A0B4C8', skin: '#F2C9A5', hair: '#B2B4B6' },
  { body: '#9FE0C2', skin: '#E0AC69', hair: '#5A3825' },
  { body: '#F7C6D9', skin: '#F1D3B3', hair: '#7A4A2A' }
]

const OTHER_SHAPES = [
  ['sq-blue', 'tri-pink', 'dot-green'],
  ['dot-yellow', 'sq-pink', 'tri-green'],
  ['tri-blue', 'dot-green', 'sq-yellow'],
  ['sq-pink', 'tri-green', 'dot-blue'],
  ['dot-green', 'tri-blue', 'sq-pink'],
  ['tri-green', 'sq-yellow', 'dot-blue'],
  ['sq-yellow', 'dot-blue', 'tri-pink'],
  ['dot-blue', 'sq-pink', 'tri-green']
]

const OTHER_SPOTS = [
  [0, 0],
  [1, 0],
  [3, 0],
  [0, 1],
  [2, 1],
  [1, 2],
  [2, 2],
  [3, 2]
]

export const TALENTS = [
  {
    id: 'lena',
    name: 'Lena',
    ...spot(1, 1),
    body: 'pink',
    skin: '#F1D3B3',
    hair: '#2B1D14',
    shapes: ['dot-pink', 'tri-yellow', 'sq-green'],
    fit: 'cs',
    front: 0
  },
  {
    id: 'aylin',
    name: 'Aylin',
    ...spot(2, 0),
    body: 'yellow',
    skin: '#C68B59',
    hair: '#2B1D14',
    shapes: ['dot-pink', 'sq-green', 'tri-blue'],
    fit: 'cs',
    front: 1
  },
  {
    id: 'mo',
    name: 'Mo',
    ...spot(0, 2),
    body: 'green',
    skin: '#8D5524',
    hair: INK,
    shapes: ['tri-yellow', 'dot-pink', 'sq-blue'],
    fit: 'cs',
    front: 2
  },
  {
    id: 'jonas',
    name: 'Jonas',
    ...spot(3, 1),
    body: '#4461D3',
    skin: '#F2C9A5',
    hair: '#7A4A2A',
    shapes: ['sq-blue', 'dot-yellow', 'tri-pink'],
    fit: 'v'
  },
  ...OTHER_SPOTS.map(([c, r], i) => ({
    id: `t${i}`,
    ...spot(c, r),
    ...OTHER_LOOKS[i],
    shapes: OTHER_SHAPES[i],
    fit: null
  }))
]

// Wer im Büro sitzt. `sad` nur bei der CS-Leitung in der Rückblende.
const STAFF = [
  {
    id: 'vLead',
    x: 4.4,
    y: 12.6,
    body: '#4461D3',
    skin: '#C68B59',
    hair: '#2B1D14',
    happy: inB('kreuz', 0.7, 0.8),
    label: 'Vertrieb'
  },
  {
    id: 'vMate',
    x: 4.4,
    y: 16.6,
    body: '#8FA4D8',
    skin: '#F1D3B3',
    hair: '#E6C27A',
    happy: inB('kreuz', 0.75, 0.85)
  },
  {
    id: 'csLead',
    x: 18.3,
    y: 11.9,
    body: 'green',
    skin: '#8D5524',
    hair: INK,
    happy: inB('einstellung', 0.86, 0.94),
    sad: [...inB('ohne', 0.74, 0.8), ...inB('rewind', 0.2, 0.5)],
    label: 'CS Lead'
  },
  {
    id: 'csMate',
    x: 21.8,
    y: 11.9,
    body: 'yellow',
    skin: '#F2C9A5',
    hair: '#7A4A2A',
    happy: inB('einstellung', 0.88, 0.96)
  },
  {
    id: 'csNew',
    x: 18.3,
    y: 16.4,
    body: '#546ED4',
    skin: '#E0AC69',
    hair: '#2B1D14',
    happy: inB('einstellung', 0.9, 0.98)
  }
]

export const STAFF_BY_ID = Object.fromEntries(STAFF.map((p) => [p.id, p]))

const LABELS = [
  { text: 'Vertrieb', x: 0.9, y: 9.4, size: 30 },
  { text: 'Customer Success', x: 16.9, y: 8.0, size: 30 },
  { text: 'Bewerbende', x: 8.8, y: 20.9, size: 28 },
  { text: 'HR', x: 8.7, y: 11.2, size: 24 }
]

// In der Rückblende gibt es noch kein Aivy, der Würfel kommt beim Zurückspulen dazu
export const AIVY_APPEARS = {
  ...pv('av', inB('rewind', 0.55, 0.85)),
  opacity: 'var(--av)'
}

// Der Scanner fährt im Regeln-Kapitel einmal über die Fläche der Bewerbenden
export const SCAN = {
  x0: 8.4,
  x1: 16,
  y0: 14.4,
  y1: 21.2,
  at: inB('regeln', 0.14, 0.7)
}
export const scanTime = (x, at = SCAN.at) => {
  const t = (x - SCAN.x0) / (SCAN.x1 - SCAN.x0)
  return at[0] + t * (at[1] - at[0])
}

export function Scanner({ at = SCAN.at }) {
  const { x0, x1, y0, y1 } = SCAN
  const a = iso(x0, y0)
  const b = iso(x0, y1)
  const dx = iso(x1, y0).x - a.x
  const dy = iso(x1, y0).y - a.y
  return (
    <g
      style={{
        ...pv('sc', at),
        ...pv('sv', [at[0] - 0.01, at[0]]),
        ...pv('sx', [at[1], at[1] + 0.01]),
        opacity: 'calc(var(--sv) * (1 - var(--sx)))',
        transform: `translate(calc(var(--sc) * ${dx}px), calc(var(--sc) * ${dy}px))`
      }}
    >
      <polygon
        points={`${a.x - 26},${a.y - 15} ${a.x},${a.y} ${b.x},${b.y} ${b.x - 26},${b.y - 15}`}
        fill='#CFE0FF'
      />
      <line
        x1={a.x}
        y1={a.y}
        x2={b.x}
        y2={b.y}
        stroke={COLORS.blue}
        strokeWidth='4'
        strokeLinecap='round'
      />
    </g>
  )
}

export function OfficeWorld4() {
  const desks = [...DESKS, { x: 2, y: 19, w: 1.3, d: 2.2 }]
  const items = [
    ...desks.map((d) => ({
      depth: d.x + (d.w || 2.4) + d.y + (d.d || 1.3),
      node: <Desk {...d} />
    })),
    // Ohne die Pflanzen, die im Vertrieb und bei den Bewerbenden im Weg stehen
    ...PLANTS.filter((p) => !(p.y > 20)).map((p) => ({
      depth: p.x + p.y + 1.6,
      node: <Plant {...p} />
    })),
    ...STAFF.map((p) => {
      const s = iso(p.x, p.y)
      return {
        depth: p.x + p.y + 0.4,
        node: (
          <g transform={`translate(${s.x} ${s.y})`}>
            <Person {...p} />
          </g>
        )
      }
    }),
    {
      depth: 12,
      node: (
        <g style={AIVY_APPEARS}>
          <Machine style={{}} />
        </g>
      )
    },
    { depth: 37.6, node: <Chair x={21.4} y={15.5} /> },
    { depth: 23.4, node: <Chair x={3.4} y={19.5} /> },
    { depth: 41, node: <Lounge /> }
  ].sort((a, b) => a.depth - b.depth)

  return (
    <g>
      <Floor />
      <Walls />
      <IsoRect x={8.4} y={14.4} w={7.6} d={6.8} fill='#FFF3D6' />
      <IsoRect x={20} y={17.4} w={6} d={4.6} fill='#FDE6EF' />
      <IsoRect x={16.6} y={8.8} w={7} d={8.6} fill='#E4F8F3' />
      <IsoRect x={0.4} y={10} w={6.2} d={11.8} fill='#E6ECFF' />
      <FloorLabels labels={LABELS} />
      <Scanner />
      {items.map((it, i) => (
        <g key={i}>{it.node}</g>
      ))}
      {STAFF.map((p) => {
        const s = iso(p.x, p.y)
        return (
          <Heart
            key={p.id}
            x={s.x}
            y={s.y - 78}
            at={inB('finale', 0.1, 0.25)}
          />
        )
      })}
    </g>
  )
}

Scanner.propTypes = { at: PropTypes.arrayOf(PropTypes.number) }

// Zeitpunkte der Version 4, andere Versionen geben eigene `times` mit
const v4Times = (talent, leaves) => ({
  happy: leaves
    ? inB('fair', 0.12, 0.2)
    : talent.id === 'lena'
      ? inB('einstellung', 0.3, 0.38)
      : inB('kreuz', 0.3, 0.4),
  ring: [scanTime(talent.x), scanTime(talent.x) + 0.008],
  phone: [inB('menschen', 0.56, 0.62), inB('menschen', 0.8, 0.86)],
  shape: (i) => inB('menschen', 0.82 + i * 0.03, 0.9 + i * 0.03),
  doc: leaves ? inB('fair', 0.04, 0.12) : null
})

/**
 * Bewerbende Person. Erscheint, wenn ihre Bewerbung gelandet ist, zeigt nach
 * den Spielen ihre Stärken, bekommt beim Scan einen Ring und am Ende das
 * Stärkenprofil.
 */
export function Talent({ talent, spawn, exitAt, times }) {
  const ring =
    talent.fit === 'cs' ? COLORS.green : talent.fit === 'v' ? COLORS.blue : null
  const t = times || v4Times(talent, !!exitAt)
  return (
    <g
      style={anim({ at: spawn, out: exitAt, y: 0, oy: 0, s: 0.3 })}
      className='off-a'
    >
      {ring && (
        <g className='off-a' style={anim({ at: t.ring, s: 0.2, y: 0 })}>
          <ellipse
            cx='0'
            cy='0'
            rx='20'
            ry='8.5'
            fill='none'
            stroke={ring}
            strokeWidth='4'
          />
        </g>
      )}
      <Person
        body={talent.body}
        skin={talent.skin}
        hair={talent.hair}
        happy={t.happy}
      />
      {/* Smartphone während der Spiele */}
      <g transform='translate(18 -44) scale(0.7)'>
        <g
          className='off-a'
          style={anim({ at: t.phone[0], out: t.phone[1], s: 0.3, y: 6 })}
        >
          <MiniPhoneSmall />
        </g>
      </g>
      {/* Stärken über dem Kopf */}
      <g transform='translate(0 -82)'>
        {talent.shapes.map((tok, i) => {
          const { kind, color } = parseShape(tok)
          return (
            <g key={tok} transform={`translate(${(i - 1) * 15 - 6} -6)`}>
              <g
                className='off-a'
                style={anim({ at: t.shape(i), s: 0.2, y: 10 })}
              >
                <Shape kind={kind} color={color} size={12} />
              </g>
            </g>
          )
        })}
      </g>
      {/* Stärkenprofil beim Abschied */}
      {t.doc && (
        <g transform='translate(-9 -112)'>
          <g className='off-a' style={anim({ at: t.doc, s: 0.3, y: 10 })}>
            <rect
              x='0'
              y='0'
              width='18'
              height='22'
              rx='3'
              fill='#FFFFFF'
              stroke={COLORS.pink}
              strokeWidth='2'
            />
            <circle cx='9' cy='8' r='3.2' fill={COLORS.pink} />
            <rect
              x='4'
              y='14'
              width='10'
              height='2.5'
              rx='1.2'
              fill='#C9D2E6'
            />
          </g>
        </g>
      )}
    </g>
  )
}

function MiniPhoneSmall() {
  return (
    <g>
      <rect x='-8' y='-13' width='16' height='26' rx='4' fill={INK} />
      <rect x='-6' y='-10' width='12' height='19' rx='2' fill='#FFFFFF' />
      <path d='M-3 2 L0 -4 L3 2 Z' fill={COLORS.yellow} />
    </g>
  )
}

Talent.propTypes = {
  talent: PropTypes.object.isRequired,
  spawn: PropTypes.arrayOf(PropTypes.number).isRequired,
  exitAt: PropTypes.arrayOf(PropTypes.number),
  times: PropTypes.object
}

/** Umriss aus drei Formen über einem freien Platz, füllt sich bei Besetzung. */
export function Silhouette({ shapes, label, fillAt, at }) {
  return (
    <div
      className='hum-sil'
      style={anim({
        at: at || inB('anforderung', 0.6, 0.74),
        y: 16,
        s: 0.5
      })}
    >
      <div className='hum-sil__shapes'>
        {shapes.map((tok) => {
          const { kind, color } = parseShape(tok)
          return (
            <span key={tok} className='hum-sil__slot'>
              <svg
                viewBox='0 0 24 24'
                width='22'
                height='22'
                aria-hidden='true'
              >
                {kind === 'tri' && (
                  <path
                    d='M12 2.5 L22 20.5 H2 Z'
                    fill='none'
                    stroke={COLORS[color]}
                    strokeWidth='2'
                    strokeDasharray='3 2.5'
                  />
                )}
                {kind === 'sq' && (
                  <rect
                    x='3'
                    y='3'
                    width='18'
                    height='18'
                    rx='2'
                    fill='none'
                    stroke={COLORS[color]}
                    strokeWidth='2'
                    strokeDasharray='3 2.5'
                  />
                )}
                {kind === 'dot' && (
                  <circle
                    cx='12'
                    cy='12'
                    r='9.5'
                    fill='none'
                    stroke={COLORS[color]}
                    strokeWidth='2'
                    strokeDasharray='3 2.5'
                  />
                )}
              </svg>
              <span
                className='hum-sil__fill'
                style={anim({ at: fillAt, s: 0.2, y: 0 })}
              >
                <Shape kind={kind} color={color} size={22} />
              </span>
            </span>
          )
        })}
      </div>
      <span className='hum-sil__label'>{label}</span>
    </div>
  )
}

Silhouette.propTypes = {
  shapes: PropTypes.arrayOf(PropTypes.string).isRequired,
  label: PropTypes.string.isRequired,
  fillAt: PropTypes.arrayOf(PropTypes.number).isRequired,
  at: PropTypes.arrayOf(PropTypes.number)
}

/** Zeitpunkte, zu denen Bewerbungen landen (je Talent). */
export const spawnAt = (i) => {
  const start = atB('menschen', 0.08 + i * 0.03)
  return [start + 0.004, start + 0.01]
}
